import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Download, ArrowLeft, CheckCircle, XCircle, MinusCircle, Eye, EyeOff, Flag } from "lucide-react";
import { RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar, ResponsiveContainer, Tooltip, ScatterChart, Scatter, XAxis, YAxis, CartesianGrid, Cell } from "recharts";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Results() {
  const { testId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [reportingQ, setReportingQ] = useState(null);
  const [reportReason, setReportReason] = useState("wrong_answer");
  const [reportComment, setReportComment] = useState("");
  const [reportedIds, setReportedIds] = useState(new Set());
  const [reportDialogOpen, setReportDialogOpen] = useState(false);

  const handleReport = async () => {
    if (!reportingQ) return;
    try {
      const res = await axios.post(`${API}/questions/${reportingQ}/report`, {
        reason: reportReason,
        comment: reportComment || undefined,
      });
      if (res.data.already_reported) {
        toast.info("Already reported — we're on it!");
      } else {
        toast.success("Question reported. Thank you!");
      }
      setReportedIds(prev => new Set([...prev, reportingQ]));
      setReportDialogOpen(false);
      setReportingQ(null);
      setReportComment("");
      setReportReason("wrong_answer");
    } catch {
      toast.error("Failed to report question");
    }
  };

  useEffect(() => {
    axios.get(`${API}/tests/${testId}/results`).then(r => { setData(r.data); setLoading(false); }).catch(() => { toast.error("Failed to load results"); setLoading(false); });
  }, [testId]);

  const downloadPdf = (type) => {
    const url = type === "paper" ? `${API}/tests/${testId}/paper-pdf` : `${API}/tests/${testId}/report-pdf`;
    window.open(url, "_blank");
  };

  if (loading) {
    return <div className="p-8 lg:p-12"><div className="h-8 w-64 bg-secondary rounded animate-pulse" /></div>;
  }

  if (!data) {
    return <div className="p-8 text-muted-foreground">No results found for this test.</div>;
  }

  const score = data.score || {};
  const topicAnalysis = data.topic_analysis || [];
  const confAnalysis = data.confidence_analysis || {};
  const qResults = data.question_results || [];

  const radarData = topicAnalysis.map(t => ({
    subject: t.topic_name?.split(" ")[0] || "",
    accuracy: t.accuracy || 0,
    fullMark: 100,
  }));

  const scatterData = qResults.filter(q => q.selected).map(q => ({
    confidence: q.confidence === "sure" ? 1 : 0,
    correct: q.is_correct ? 1 : 0,
    name: `Q${qResults.indexOf(q) + 1}`,
  }));

  const confScatterFormatted = [
    { label: "Sure & Correct", x: 1, y: 1, count: scatterData.filter(d => d.confidence === 1 && d.correct === 1).length, color: "#10b981" },
    { label: "Sure & Wrong", x: 1, y: 0, count: scatterData.filter(d => d.confidence === 1 && d.correct === 0).length, color: "#ef4444" },
    { label: "Guessed & Correct", x: 0, y: 1, count: scatterData.filter(d => d.confidence === 0 && d.correct === 1).length, color: "#f59e0b" },
    { label: "Guessed & Wrong", x: 0, y: 0, count: scatterData.filter(d => d.confidence === 0 && d.correct === 0).length, color: "#8b5cf6" },
  ].filter(d => d.count > 0);

  const filteredQResults = filter === "all" ? qResults : filter === "wrong" ? qResults.filter(q => q.status === "incorrect") : qResults.filter(q => q.status === "unattempted");

  return (
    <div className="p-8 lg:p-12 space-y-8 animate-fade-in" data-testid="results-page">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <Button data-testid="back-to-dashboard-btn" variant="ghost" size="sm" className="mb-2 -ml-2" onClick={() => navigate("/")}>
            <ArrowLeft size={16} className="mr-1" /> Dashboard
          </Button>
          <h1 className="font-heading text-4xl md:text-5xl font-bold tracking-tight">Test Results</h1>
          <p className="text-muted-foreground mt-1">{data.test_info?.test_code || "N/A"}</p>
        </div>
        <div className="flex gap-2">
          <Button data-testid="download-paper-btn" variant="outline" size="sm" onClick={() => downloadPdf("paper")} className="gap-1">
            <Download size={14} /> Paper
          </Button>
          <Button data-testid="download-report-btn" variant="outline" size="sm" onClick={() => downloadPdf("report")} className="gap-1">
            <Download size={14} /> Report
          </Button>
        </div>
      </div>

      {/* Score Summary */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        {[
          { label: "Score", value: `${score.marks_obtained?.toFixed(1)}/${score.max_marks}`, sub: `${score.percentage?.toFixed(1)}%` },
          { label: "Correct", value: score.correct, icon: CheckCircle, color: "text-emerald-400" },
          { label: "Wrong", value: score.incorrect, icon: XCircle, color: "text-red-400" },
          { label: "Skipped", value: score.unattempted, icon: MinusCircle, color: "text-muted-foreground" },
          { label: "Neg. Marks", value: (score.incorrect * 0.67).toFixed(1), color: "text-red-400" },
        ].map((s) => (
          <Card key={s.label} className="bg-card border-border" data-testid={`score-${s.label.toLowerCase()}`}>
            <CardContent className="p-4 text-center">
              <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">{s.label}</p>
              <p className={`font-mono text-2xl font-bold ${s.color || ""}`}>{s.value}</p>
              {s.sub && <p className="text-xs text-muted-foreground mt-0.5">{s.sub}</p>}
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Topic Radar */}
        <Card className="bg-card border-border" data-testid="results-radar-chart">
          <CardHeader className="pb-2">
            <CardTitle className="font-heading text-lg">Topic Accuracy</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <RadarChart data={radarData}>
                <PolarGrid stroke="hsl(var(--border))" />
                <PolarAngleAxis dataKey="subject" tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 11 }} />
                <PolarRadiusAxis angle={90} domain={[0, 100]} tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 9 }} />
                <Radar name="Accuracy" dataKey="accuracy" stroke="#6366f1" fill="#6366f1" fillOpacity={0.2} strokeWidth={2} />
                <Tooltip contentStyle={{ background: "#121214", border: "1px solid #27272a", borderRadius: "6px", color: "#fafafa", fontSize: "12px" }} />
              </RadarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Confidence vs Accuracy */}
        <Card className="bg-card border-border" data-testid="confidence-accuracy-chart">
          <CardHeader className="pb-2">
            <CardTitle className="font-heading text-lg">Confidence vs Accuracy</CardTitle>
            <p className="text-xs text-muted-foreground">Bubble size = number of questions</p>
          </CardHeader>
          <CardContent>
            <div className="h-[300px] flex items-center justify-center">
              <div className="grid grid-cols-2 gap-6 w-full max-w-sm">
                {confScatterFormatted.map((d) => (
                  <div key={d.label} className="text-center p-4 rounded-sm border border-border">
                    <div className="w-16 h-16 rounded-full mx-auto mb-2 flex items-center justify-center font-mono text-xl font-bold" style={{ backgroundColor: `${d.color}20`, color: d.color, border: `2px solid ${d.color}` }}>
                      {d.count}
                    </div>
                    <p className="text-xs text-muted-foreground">{d.label}</p>
                  </div>
                ))}
              </div>
            </div>
            <div className="mt-4 grid grid-cols-2 gap-4 text-center">
              <div>
                <p className="text-xs text-muted-foreground">Sure Accuracy</p>
                <p className="font-mono text-lg font-bold text-emerald-400">{confAnalysis.sure_accuracy?.toFixed(0) || 0}%</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Guess Accuracy</p>
                <p className="font-mono text-lg font-bold text-amber-400">{confAnalysis.guessed_accuracy?.toFixed(0) || 0}%</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Topic Breakdown Table */}
      <Card className="bg-card border-border" data-testid="topic-breakdown">
        <CardHeader className="pb-2">
          <CardTitle className="font-heading text-lg">Topic Breakdown</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border text-muted-foreground text-xs uppercase tracking-wider">
                  <th className="text-left py-2 px-3">Topic</th>
                  <th className="text-center py-2 px-3">Total</th>
                  <th className="text-center py-2 px-3">Correct</th>
                  <th className="text-center py-2 px-3">Wrong</th>
                  <th className="text-center py-2 px-3">Skipped</th>
                  <th className="text-center py-2 px-3">Accuracy</th>
                </tr>
              </thead>
              <tbody>
                {topicAnalysis.map((t) => (
                  <tr key={t.topic_id} className="border-b border-border/50 hover:bg-secondary/30 transition-colors">
                    <td className="py-2.5 px-3 font-medium">{t.topic_name}</td>
                    <td className="text-center py-2.5 px-3 font-mono">{t.total}</td>
                    <td className="text-center py-2.5 px-3 font-mono text-emerald-400">{t.correct}</td>
                    <td className="text-center py-2.5 px-3 font-mono text-red-400">{t.incorrect}</td>
                    <td className="text-center py-2.5 px-3 font-mono text-muted-foreground">{t.unattempted}</td>
                    <td className="text-center py-2.5 px-3">
                      <Badge variant="outline" className={`font-mono text-xs ${
                        t.accuracy >= 60 ? "border-emerald-500/50 text-emerald-400" : t.accuracy >= 40 ? "border-amber-500/50 text-amber-400" : "border-red-500/50 text-red-400"
                      }`}>
                        {t.accuracy?.toFixed(0)}%
                      </Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Question Review */}
      <Card className="bg-card border-border" data-testid="question-review">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="font-heading text-lg">Question Review</CardTitle>
            <div className="flex gap-2">
              {["all", "wrong", "unattempted"].map(f => (
                <Button key={f} size="sm" variant={filter === f ? "default" : "outline"} data-testid={`filter-${f}`} onClick={() => setFilter(f)} className={`text-xs capitalize ${filter === f ? "bg-primary" : ""}`}>
                  {f} {f !== "all" && `(${f === "wrong" ? qResults.filter(q => q.status === "incorrect").length : qResults.filter(q => q.status === "unattempted").length})`}
                </Button>
              ))}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <ScrollArea className="h-[500px]">
            <div className="space-y-4">
              {filteredQResults.map((q, i) => (
                <div key={q.question_id} data-testid={`review-q-${i}`} className="p-4 rounded-sm border border-border bg-secondary/20">
                  <div className="flex items-start justify-between mb-2">
                    <p className="text-sm leading-relaxed flex-1 whitespace-pre-line">{q.question_text}</p>
                    <Badge className={`ml-2 shrink-0 ${q.status === "correct" ? "bg-emerald-500/20 text-emerald-400" : q.status === "incorrect" ? "bg-red-500/20 text-red-400" : "bg-secondary text-muted-foreground"}`}>
                      {q.status}
                    </Badge>
                  </div>
                  <div className="grid grid-cols-2 gap-2 mt-3 text-xs">
                    {q.options && Object.entries(q.options).map(([key, text]) => (
                      <div key={key} className={`p-2 rounded-sm border ${
                        key === q.correct_answer ? "border-emerald-500/50 bg-emerald-500/5" : key === q.selected && key !== q.correct_answer ? "border-red-500/50 bg-red-500/5" : "border-border/50"
                      }`}>
                        <span className="font-mono">{key}.</span> {text}
                        {key === q.correct_answer && <CheckCircle size={12} className="inline ml-1 text-emerald-400" />}
                        {key === q.selected && key !== q.correct_answer && <XCircle size={12} className="inline ml-1 text-red-400" />}
                      </div>
                    ))}
                  </div>
                  {q.explanation && (
                    <div className="mt-3 p-3 rounded-sm bg-primary/5 border border-primary/20 text-xs">
                      <p className="font-semibold text-primary mb-1">Explanation:</p>
                      <p className="text-muted-foreground leading-relaxed">{q.explanation}</p>
                      {q.source_citations?.length > 0 && (
                        <p className="mt-1 text-primary/60 italic">Source: {q.source_citations.join(", ")}</p>
                      )}
                    </div>
                  )}
                  {q.confidence && q.selected && (
                    <div className="mt-2 flex items-center gap-1 text-xs text-muted-foreground">
                      {q.confidence === "sure" ? <Eye size={12} /> : <EyeOff size={12} />}
                      <span className="capitalize">{q.confidence}</span>
                    </div>
                  )}
                  <div className="mt-2 flex justify-end">
                    <Button
                      variant="ghost"
                      size="sm"
                      data-testid={`report-q-${i}`}
                      className="text-xs text-muted-foreground hover:text-red-400 gap-1"
                      disabled={reportedIds.has(q.question_id)}
                      onClick={(e) => {
                        e.stopPropagation();
                        setReportingQ(q.question_id);
                        setReportDialogOpen(true);
                      }}
                    >
                      <Flag size={12} />
                      {reportedIds.has(q.question_id) ? "Reported" : "Report Incorrect"}
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </ScrollArea>
        </CardContent>
      </Card>

      {/* Report Question Dialog */}
      <Dialog open={reportDialogOpen} onOpenChange={setReportDialogOpen}>
        <DialogContent className="bg-card border-border" data-testid="report-dialog" aria-describedby="report-dialog-desc">
          <DialogHeader>
            <DialogTitle className="font-heading">Report Incorrect Question</DialogTitle>
            <p id="report-dialog-desc" className="text-sm text-muted-foreground">Help us improve by reporting questions with incorrect answers or flawed explanations.</p>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm text-muted-foreground mb-1 block">Reason</label>
              <Select value={reportReason} onValueChange={setReportReason}>
                <SelectTrigger data-testid="report-reason-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="wrong_answer">Wrong Answer Key</SelectItem>
                  <SelectItem value="explanation_mismatch">Explanation contradicts answer</SelectItem>
                  <SelectItem value="factual_error">Factual error in question/options</SelectItem>
                  <SelectItem value="ambiguous">Ambiguous / Multiple correct answers</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm text-muted-foreground mb-1 block">Additional comments (optional)</label>
              <Textarea
                data-testid="report-comment-input"
                value={reportComment}
                onChange={(e) => setReportComment(e.target.value)}
                placeholder="e.g., Statement 3 is also correct because..."
                rows={3}
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" size="sm" onClick={() => setReportDialogOpen(false)}>Cancel</Button>
              <Button data-testid="report-submit-btn" size="sm" className="bg-red-600 hover:bg-red-700 text-white" onClick={handleReport}>
                <Flag size={14} className="mr-1" /> Submit Report
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
